import React from 'react';
import {
  ScheduledNotificationStatus,
} from '@shared/enums/ScheduledNotificationStatus';
import { CsvError } from '@shared/interfaces/ScheduledNotification';

/**
 * Validates if a date is in the future
 * 
 * @param date The date to validate
 * @returns boolean indicating if the date is valid (true) or invalid (false)
 * 
 * TODO: Future enhancement - modify the DateTimePickerDropdown component to directly 
 * disable past dates in the calendar by adding a disablePast prop
 */
export const isFutureDate = (date?: Date): boolean => {
  if (!date) return false;
  const now = new Date();
  return date > now;
};

/**
 * Utility function to handle editing a section in the notification form
 * Focuses and scrolls to the appropriate section of the form when editing from preview
 * 
 * @param section The section to edit: 'message', 'recipients' , or 'schedule'
 * @param testID The base testID to use for finding elements
 * @param setShowPreview Function to update preview visibility state (for NotificationAddForm)
 * @param setShowEditForm Function to update edit form visibility state (for NotificationDetailModal)
 */
export const handleEditSection = (
  section: 'message' | 'recipients' | 'schedule',
  testID: string,
  setShowPreview?: React.Dispatch<React.SetStateAction<boolean>>,
  setShowEditForm?: React.Dispatch<React.SetStateAction<boolean>>,
): void => {
  // Update visibility state based on which component called this
  if (setShowPreview) {
    setShowPreview(false);
  }
  
  if (setShowEditForm) {
    setShowEditForm(true);
  }
  
  // Give time for the form to render before attempting to focus/scroll
  setTimeout(() => {
    if (section === 'message') {
      const messageElement = document.getElementById(`${testID}-content-card`);
      if (messageElement) {
        messageElement.focus();
        messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        highlightElement(messageElement);
      }
    } else if (section === 'recipients') {
      const recipientElement = document.getElementById(`${testID}-recipient-section`);
      if (recipientElement) {
        recipientElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        highlightElement(recipientElement);
      }
    } else if (section === 'schedule') {
      const scheduleElement = document.getElementById(`${testID}-schedule-section`);
      if (scheduleElement) {
        scheduleElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        highlightElement(scheduleElement);
      }
    }
  }, 100);
};

// Helper to temporarily add a bold outline and font-weight to an element to highlight it
const highlightElement = (el: HTMLElement, duration = 2000) => {
  if (!el) return;

  // store previous inline styles so we can restore them
  const prevOutline = el.style.outline;
  const prevOutlineOffset = el.style.outlineOffset;
  const prevFontWeight = el.style.fontWeight;

  // apply highlight styles (a bold outline and bold text)
  el.style.outline = '1px solid #67790F';
  el.style.outlineOffset = '2px';
  el.style.fontWeight = '700';

  // remove highlight after the duration and restore previous inline styles
  setTimeout(() => {
    el.style.outline = prevOutline;
    el.style.outlineOffset = prevOutlineOffset;
    el.style.fontWeight = prevFontWeight;
  }, duration);
};

// Returns an array of missing required field keys depending on the target status
export const getMissingRequiredFields = (params: {
  targetStatus: ScheduledNotificationStatus,
  retailerId?: unknown,
  title?: string,
  communicationType?: unknown,
  message?: string,
  sendDate?: Date | undefined,
  recipientCountState?: number | null,
}): string[] => {
  const {
    targetStatus,
    retailerId,
    title,
    communicationType,
    message,
    sendDate,
    recipientCountState,
  } = params;

  const missing: string[] = [];

  if (!retailerId) missing.push('retailer');
  if (!title) missing.push('title');
  if (!communicationType) missing.push('communicationType');

  if (targetStatus === ScheduledNotificationStatus.Scheduled) {
    if (!message || !message.trim()) missing.push('message');
    if (!sendDate) missing.push('sendDate');
    const isRecipientCountInvalid = (
      recipientCountState === null ||
      typeof recipientCountState !== 'number'
    );
    if (isRecipientCountInvalid) missing.push('recipientCount');
  }

  return missing;
};

/**
 * Downloads CSV errors as a CSV file
 * 
 * @param csvErrors Array of CSV error objects containing error details
 */
export const downloadCsvErrors = (csvErrors: CsvError[], filename = 'upload-errors'): void => {
  if (!csvErrors || csvErrors.length === 0) return;

  const errorCsvData = csvErrors.map(error => ({
    'Error Message': error.errMessage,
    'Error Type': error.errorType,
    'Row': error.row,
  }));

  // Convert to CSV format
  const csvHeaders = Object.keys(errorCsvData[0]).join(',');
  const csvRows = errorCsvData.map(row =>
    Object.values(row).map(value => `"${value}"`).join(','),
  ).join('\n');
  const csvContent = `${csvHeaders}\n${csvRows}`;

  // Download the error CSV
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}-${new Date().toISOString().slice(0, 10)}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
